import { useState, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useUserContext } from '@/context/user.context';

import { RoutesEnum } from '@/enum/routes.enum';
import { SearchParamsEnum } from '@/enum/searchParams.enum';
import { StorageKeysEnum } from '@/enum/storageKeys.enum';

import { useJWT } from '@/hooks/useJWT';
import { useLocalStorage } from '@/hooks/useLocalStorage';

import { ToastTypesEnum } from '../enum/toastTypes.enum';
import { tKeys } from '../localization';
import { useLoginWithTokenMutation } from '../services/api.service';
import { buildRedirectLink } from '../utils';
import { takeRedirectionLink } from '../utils/ssoRedirect';

import { useToast } from './useToast';

const AUTH_PAGES: Array<string> = [RoutesEnum.PROFILE];

export const useAuth = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { jwt, setJWT } = useJWT();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoginLoading, setIsLoginLoading } = useUserContext();

  const { setItem: setUserIDValue } = useLocalStorage<number>(
    StorageKeysEnum.user_id,
  );
  const { setItem: setUsername } = useLocalStorage<string>(
    StorageKeysEnum.username,
  );
  const { setItem: setTmpTokenValue } = useLocalStorage<string>(
    StorageKeysEnum.tmpToken,
  );
  const { value: redirectURL, removeItem: removeRedirectURL } =
    useLocalStorage<string>(StorageKeysEnum.redirectURL);
  const { value: lang } = useLocalStorage<string>(StorageKeysEnum.i18nextLng);

  const token = searchParams.get(SearchParamsEnum.token);

  const isNeedAuth = AUTH_PAGES.includes(pathname);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsernameState] = useState('');

  // API
  const [triggerLogin] = useLoginWithTokenMutation();

  const handleLogin = useCallback(async () => {
    if (!token || isLoginLoading) return;

    setIsLoginLoading(true);

    try {
      const data = await triggerLogin({ token }).unwrap();

      setJWT(data?.token);
      setUserIDValue(data?.user_id);
      setTmpTokenValue(data?.tmp_token);

      toast(t(tKeys.successfullyLoggedIn), ToastTypesEnum.success);

      const link = takeRedirectionLink(data);
      if (link) {
        return navigate(link);
      }

      if (redirectURL) {
        const rUrl = redirectURL;
        // NOTE: commented to resolve redirection on GZ after refreshing page
        removeRedirectURL();
        return window.location.assign(
          buildRedirectLink(rUrl, data?.tmp_token, lang, token),
        );
      }
      navigate(RoutesEnum.HOME);
    } catch (e) {
      toast(t(tKeys.errorDuringLogin), ToastTypesEnum.error);
      navigate(RoutesEnum.HOME);
    } finally {
      setIsLoginLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, redirectURL, lang]);

  // Set username from user context when available
  useEffect(() => {
    if (user?.username) {
      setUsernameState(user.username);
      setUsername(user.username);
    }
  }, [user, setUsername]);

  useEffect(() => {
    if (isNeedAuth && !jwt) navigate(RoutesEnum.HOME);
  });

  useEffect(() => {
    const token = localStorage.getItem(StorageKeysEnum.jwt);
    const userId = localStorage.getItem(StorageKeysEnum.user_id);
    const storedUsername = localStorage.getItem(StorageKeysEnum.username);

    if (token && userId) {
      setIsAuthenticated(true);
      if (storedUsername) {
        setUsernameState(storedUsername);
      }
    }
  }, [jwt]);

  const logout = () => {
    localStorage.removeItem(StorageKeysEnum.jwt);
    localStorage.removeItem(StorageKeysEnum.user_id);
    localStorage.removeItem(StorageKeysEnum.username);
    setIsAuthenticated(false);
    setUsernameState('');
    navigate('/');
  };

  return { isAuthenticated, username, logout, handleLogin };
};
